import * as React from 'react';
import { useState } from 'react';
import * as API from '../../Utils/API';
import { htmlIf, maybeHtml } from "../../Utils/HTML";
import { CopilotProfileProps } from './CopilotProfile';
import { DismissibleModal } from '../../Components/Modal';
import { CopilotEmploymentLine, EmploymentType } from '../../Types/Copilot';
import PaginatedItems from '../../Components/PaginatedItems';
import MonthYearSelect from '../../Components/MonthYearSelect';

// Courtesy of ChatGPT
function sortEmploymentHistory(employmentLines: CopilotEmploymentLine[]) {
  return employmentLines.sort((a, b) => {
    // Handle ongoing jobs (undefined end date) — they should come first
    if (!a.endYear && !b.endYear) {
      // If both are ongoing, sort by earliest start date
      return compareStartDates(a, b);
    }
    if (!a.endYear) return -1; // a is ongoing, so it comes first
    if (!b.endYear) return 1;  // b is ongoing, so a comes first

    // Compare end dates (latest first)
    if (b.endYear !== a.endYear) return b.endYear - a.endYear;
    if (b.endMonth && a.endMonth && b.endMonth !== a.endMonth) {
      return b.endMonth - a.endMonth;
    }

    // If end dates are the same, compare start dates (earliest first)
    return compareStartDates(a, b);
  });
}

// Courtesy of ChatGPT
function compareStartDates(a: CopilotEmploymentLine, b: CopilotEmploymentLine) {
  if (!a.startYear || !b.startYear) return 0; // If missing start date, keep order
  if (a.startYear !== b.startYear) return a.startYear - b.startYear;
  if (a.startMonth && b.startMonth) return a.startMonth - b.startMonth;
  return 0;
}

// Courtesy of ChatGPT
const formatEmploymentDates = ({ startMonth, startYear, endMonth, endYear }: CopilotEmploymentLine): string => {
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const format = (month?: number, year?: number) => year ? `${month ? months[month - 1] + " " : ""}${year}` : "Present";

  return `${format(startMonth, startYear)} – ${format(endMonth, endYear)}`;
};

export const ReadOnly = (props: CopilotProfileProps) => (
  <div className="card mt-3 rounded-2">
    <div className="card-body py-3">
      <div className="fs-xl fw-bold text-dark">Employment history</div>
      <div className="mt-2">
        {props.employmentHistory.length >= 5
        ? <PaginatedItems
            allItems={sortEmploymentHistory(props.employmentHistory)}
            numItemsPerPage={5}
            renderItem={(line) => (
              <div className="py-2 border-bottom" key={`education-line-${line.id}`}>
                {RenderLineContent(line)}
              </div>
            )}
          />
        : sortEmploymentHistory(props.employmentHistory).map((line) => (
            <div className="py-2 border-bottom" key={`education-line-${line.id}`}>
              {RenderLineContent(line)}
            </div>
          ))
        }
      </div>
    </div>
  </div>
)

const RenderLineContent = (line: CopilotEmploymentLine) => (
  <div className="">
    <div className="fs-md fw-semibold text-dark">{line.title} | {line.company}</div>
    {maybeHtml(line.employmentType, (employmentType) => (
      <div className="fs-md text-gray-600 mt-1">
        {employmentType.replace("-", " ").replace(/\b\w/g, (char) => char.toUpperCase())}
      </div>
    ))}
    {maybeHtml(line.startYear, () => (
      <div className="fs-md text-gray-600 mt-1">
        {formatEmploymentDates(line)}
      </div>
    ))}
  </div>
)

enum Modal
  { NoModal
  , AddEmploymentModal
  , EditEmploymentModal
  , ConfirmDeleteEmploymentModal
  }

export const Edit = (props: CopilotProfileProps, onUpdate: (CopilotProfileProps) => void) => {
  const [isSaving, setIsSaving] = useState(false);

  const [modal, setModal] = useState(Modal.NoModal);

  const [lineToEdit, setLineToEdit] = useState<CopilotEmploymentLine>();

  const [company, setCompany] = useState<string>();
  const [title, setTitle] = useState<string>();
  const [employmentType, setEmploymentType] = useState<EmploymentType>();
  const [startMonth, setStartMonth] = useState<number>();
  const [startYear, setStartYear] = useState<number>();
  const [endMonth, setEndMonth] = useState<number>();
  const [endYear, setEndYear] = useState<number>();

  const [showErrors, setShowErrors] = useState(false);

  function clearStateLineFields() {
    setLineToEdit(undefined);
    setCompany(undefined);
    setTitle(undefined);
    setEmploymentType(undefined);
    setStartMonth(undefined);
    setStartYear(undefined);
    setEndMonth(undefined);
    setEndYear(undefined);
  }

  function startEditingLine(line: CopilotEmploymentLine) {
    setLineToEdit(line);
    setCompany(line.company);
    setTitle(line.title);
    setEmploymentType(line.employmentType);
    setStartMonth(line.startMonth);
    setStartYear(line.startYear);
    setEndMonth(line.endMonth);
    setEndYear(line.endYear);

    setModal(Modal.EditEmploymentModal);
  }

  function validateInputs() {
    const isValid =
      !(endYear &&
        startYear &&
        (endYear < startYear || (endYear === startYear && endMonth && startMonth && endMonth < startMonth))) &&
      company.length > 0 &&
      title.length > 0;

    if (!isValid) {
      setShowErrors(true);
    }

    return isValid;
  }

  function addEmploymentLine() {
    if (!validateInputs()) return;

    setIsSaving(true);

    const postBody = {
      company: company,
      title: title,
      employmentType: employmentType,
      startMonth: startMonth,
      startYear: startYear,
      endMonth: endMonth,
      endYear: endYear
    }

    API.post("api_copilot_add_employment_line_path", postBody).then(function (result) {
      onUpdate(result);
      setModal(Modal.NoModal);
      clearStateLineFields();
      setIsSaving(false);
    })
  }

  function deleteEmploymentLine() {
    setIsSaving(true);

    const postBody = { id: lineToEdit.id }

    API.post("api_copilot_delete_employment_line_path", postBody).then(function (result) {
      onUpdate(result);
      if (modal === Modal.ConfirmDeleteEmploymentModal) {
        setModal(Modal.NoModal);
        setIsSaving(false);
      } else if (modal === Modal.EditEmploymentModal) {
        // Don't do anything — we've deleted the original version of the line, but we still need to add the new one.
      }
    })
  }

  function saveEdits() {
    if (!validateInputs()) return;

    deleteEmploymentLine();
    addEmploymentLine();
  }

  const ViewModal = () => {
    const InputBody = () => (
      <div className="">
        <div className="mt-2">
          <h5 className="mb-1">Job Title*</h5>
          <input
            onChange={(e) => setTitle(e.target.value)}
            className="form-control t--title"
            value={title}
            placeholder='i.e. Graphic Designer'
          />
          {htmlIf(showErrors && !(title.length > 0),
            <div className="text-danger fw-semibold mt-1">
              Please enter a job title.
            </div>
          )}
        </div>
        <div className="mt-2">
          <h5 className="mb-1">Company/Organization*</h5>
          <input
            onChange={(e) => setCompany(e.target.value)}
            className="form-control t--company"
            value={company}
            placeholder='i.e. Lucia'
          />
          {htmlIf(showErrors && !(company.length > 0),
            <div className="text-danger fw-semibold mt-1">
              Please enter a company or organization name.
            </div>
          )}
        </div>
        <div className="mt-2">
          <h5 className="mb-1">Employment Type</h5>
          <select className="form-select t--employment-type" value={employmentType || ""} onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}>
            <option value="" disabled>
              Select an option…
            </option>
            {Object.values(EmploymentType).map((type) => (
              <option key={type} value={type}>
                {type.replace("-", " ").replace(/\b\w/g, (char) => char.toUpperCase())} {/* Convert to Title Case */}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-2">
          <h5 className="mb-1">Start Date</h5>
          <MonthYearSelect
            month={startMonth}
            year={startYear}
            setMonth={setStartMonth}
            setYear={setStartYear}
          />
        </div>
        <div className="mt-2">
          <h5 className="mb-1">End Date</h5>
          <MonthYearSelect
            month={endMonth}
            year={endYear}
            setMonth={setEndMonth}
            setYear={setEndYear}
          />
          {/* Courtesy of ChatGPT */}
          {htmlIf(
            (endYear && startYear &&
              (endYear < startYear || (endYear === startYear && endMonth && startMonth && endMonth < startMonth))
            ),
            <div className="text-danger fw-semibold mt-1">
              End date cannot be before start date!
            </div>
          )}
        </div>
      </div>
    )

    switch(modal) {
      case Modal.NoModal:
        return null
      case Modal.AddEmploymentModal:
        return (
          <DismissibleModal
            dialogClass='modal-lg'
            title={<h4>Employment</h4>}
            body={InputBody()}
            footer={
              <div>
                <button className="btn btn-primary px-4 t--save-employment-line" onClick={addEmploymentLine} disabled={isSaving}>Save</button>
              </div>
            }
            onDismiss={() => setModal(Modal.NoModal)}
          />
        )
      case Modal.EditEmploymentModal:
        return (
          <DismissibleModal
            dialogClass='modal-lg'
            title={<h4>Employment</h4>}
            body={InputBody()}
            footer={
              <div>
                <button className="btn btn-primary px-4 t--edit-employment-line" onClick={saveEdits} disabled={isSaving}>Save</button>
              </div>
            }
            onDismiss={() => {
              setModal(Modal.NoModal);
              clearStateLineFields();
            }}
          />
        )
      case Modal.ConfirmDeleteEmploymentModal:
        return (
          <DismissibleModal
            title={<h4>Confirm Removal</h4>}
            body={
              <div>
                <div className="mb-2">
                  Are you sure you’d like to delete this employment line from your profile?
                </div>
                <div className="">
                  <button className="btn btn-danger w-100 mb-2" onClick={deleteEmploymentLine}>Remove</button>
                  <button className="btn btn-outline-primary w-100" onClick={() => setModal(Modal.NoModal)}>Keep</button>
                </div>
              </div>
            }
            onDismiss={() => setModal(Modal.NoModal)}
          />
        )
    }
  }

  return (
    <div className="card mt-3 rounded-2">
      {ViewModal()}
      <div className="card-body py-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="fs-xl fw-bold text-dark">Employment history</div>
          <button className="btn btn-link p-0 t--edit-skills" onClick={() => setModal(Modal.AddEmploymentModal)}>
            <i className="ai-circle-plus me-1" />
            Add employment
          </button>
        </div>
        {props.employmentHistory.length > 0
        ? <div className="mt-2">
            <PaginatedItems
              allItems={sortEmploymentHistory(props.employmentHistory)}
              numItemsPerPage={5}
              renderItem={(line) => (
                <div className="py-2 border-bottom" key={`employment-line-${line.id}`}>
                  <div className="d-flex align-items-start justify-content-between">
                    {RenderLineContent(line)}
                    <div className="d-flex">
                      <button className="btn btn-link p-0 me-2" onClick={() => startEditingLine(line)}>
                        <i className="ai-edit fs-xl" />
                      </button>
                      <button className="btn btn-link text-danger p-0"
                        onClick={() => {
                          setModal(Modal.ConfirmDeleteEmploymentModal);
                          setLineToEdit(line)
                        }}
                      >
                        <i className="ai-trash fs-xl" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        : <div className="text-center py-2 mt-2 text-dark">
            <img src='/img/dashboard/receipt.png' width={64} className="" />
            <div className="mt-1 fs-md fw-semibold">Add employment history</div>
            <div className="fs-md">Show your experience and build trust with potential clients.</div>
          </div>
        }
      </div>
    </div>
  )
}
